import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import SettingsSectionLoading from "./settings-section-loading";
import { RefreshToolbarButton } from "./refresh-toolbar-button";
import {
  AppSettings,
  UpdateAppSettingsPayload,
  getAppSettings,
  updateAppSettings,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

type MobileAppFormState = {
  iosLatestVersion: string;
  iosMinSupportedVersion: string;
  iosForceUpdate: boolean;
  androidLatestVersion: string;
  androidMinSupportedVersion: string;
  androidForceUpdate: boolean;
  maintenanceMode: boolean;
  iosStoreUrl: string;
  androidStoreUrl: string;
  supportPhoneNumber: string;
  supportEmail: string;
  supportWhatsApp: string;
  supportHours: string;
  maintenanceMessage: string;
  privacyPolicyUrl: string;
  termsOfServiceUrl: string;
  websiteUrl: string;
  facebookUrl: string;
  instagramUrl: string;
  twitterUrl: string;
  linkedInUrl: string;
  aboutText: string;
};

const nullToEmpty = (value: string | null | undefined) => value ?? "";

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const settingsToForm = (settings: AppSettings): MobileAppFormState => ({
  iosLatestVersion: settings.iosLatestVersion,
  iosMinSupportedVersion: settings.iosMinSupportedVersion,
  iosForceUpdate: settings.iosForceUpdate,
  androidLatestVersion: settings.androidLatestVersion,
  androidMinSupportedVersion: settings.androidMinSupportedVersion,
  androidForceUpdate: settings.androidForceUpdate,
  maintenanceMode: settings.maintenanceMode,
  iosStoreUrl: nullToEmpty(settings.iosStoreUrl),
  androidStoreUrl: nullToEmpty(settings.androidStoreUrl),
  supportPhoneNumber: nullToEmpty(settings.supportPhoneNumber),
  supportEmail: nullToEmpty(settings.supportEmail),
  supportWhatsApp: nullToEmpty(settings.supportWhatsApp),
  supportHours: nullToEmpty(settings.supportHours),
  maintenanceMessage: nullToEmpty(settings.maintenanceMessage),
  privacyPolicyUrl: nullToEmpty(settings.privacyPolicyUrl),
  termsOfServiceUrl: nullToEmpty(settings.termsOfServiceUrl),
  websiteUrl: nullToEmpty(settings.websiteUrl),
  facebookUrl: nullToEmpty(settings.facebookUrl),
  instagramUrl: nullToEmpty(settings.instagramUrl),
  twitterUrl: nullToEmpty(settings.twitterUrl),
  linkedInUrl: nullToEmpty(settings.linkedInUrl),
  aboutText: nullToEmpty(settings.aboutText),
});

const formToPayload = (form: MobileAppFormState): UpdateAppSettingsPayload => ({
  iosLatestVersion: form.iosLatestVersion.trim(),
  iosMinSupportedVersion: form.iosMinSupportedVersion.trim(),
  iosForceUpdate: form.iosForceUpdate,
  androidLatestVersion: form.androidLatestVersion.trim(),
  androidMinSupportedVersion: form.androidMinSupportedVersion.trim(),
  androidForceUpdate: form.androidForceUpdate,
  maintenanceMode: form.maintenanceMode,
  iosStoreUrl: emptyToNull(form.iosStoreUrl),
  androidStoreUrl: emptyToNull(form.androidStoreUrl),
  supportPhoneNumber: emptyToNull(form.supportPhoneNumber),
  supportEmail: emptyToNull(form.supportEmail),
  supportWhatsApp: emptyToNull(form.supportWhatsApp),
  supportHours: emptyToNull(form.supportHours),
  maintenanceMessage: emptyToNull(form.maintenanceMessage),
  privacyPolicyUrl: emptyToNull(form.privacyPolicyUrl),
  termsOfServiceUrl: emptyToNull(form.termsOfServiceUrl),
  websiteUrl: emptyToNull(form.websiteUrl),
  facebookUrl: emptyToNull(form.facebookUrl),
  instagramUrl: emptyToNull(form.instagramUrl),
  twitterUrl: emptyToNull(form.twitterUrl),
  linkedInUrl: emptyToNull(form.linkedInUrl),
  aboutText: emptyToNull(form.aboutText),
});

const SettingsSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="border border-gray-200 rounded-xl p-5 bg-white space-y-4">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {children}
  </section>
);

const SwitchField: React.FC<{
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ id, label, description, checked, onCheckedChange }) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <Label htmlFor={id} className="font-medium">
        {label}
      </Label>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

const MobileAppSettingsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MobileAppFormState | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ["app-settings"],
    queryFn: getAppSettings,
  });

  useEffect(() => {
    if (!settingsQuery.error) return;
    toast.error(getBackendErrorMessage(settingsQuery.error, "Failed to load app settings"));
  }, [settingsQuery.error]);

  useEffect(() => {
    if (settingsQuery.data) {
      setForm(settingsToForm(settingsQuery.data));
    }
  }, [settingsQuery.data]);

  const updateField = <K extends keyof MobileAppFormState>(
    key: K,
    value: MobileAppFormState[K],
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!form) return;
    if (!form.iosLatestVersion.trim() || !form.iosMinSupportedVersion.trim()) {
      return toast.error("iOS version fields are required");
    }
    if (!form.androidLatestVersion.trim() || !form.androidMinSupportedVersion.trim()) {
      return toast.error("Android version fields are required");
    }

    setIsSaving(true);
    try {
      await updateAppSettings(formToPayload(form));
      toast.success("Mobile app settings saved");
      await queryClient.invalidateQueries({ queryKey: ["app-settings"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save app settings"));
    } finally {
      setIsSaving(false);
    }
  };

  if (settingsQuery.isLoading || !form) {
    return <SettingsSectionLoading />;
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Configure mobile app versions, maintenance mode, support contacts, and links shown in
          the app.
        </p>
        <RefreshToolbarButton
          isRefreshing={settingsQuery.isFetching}
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ["app-settings"] })}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SettingsSection title="iOS">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ios-latest">Latest version</Label>
            <Input
              id="ios-latest"
              value={form.iosLatestVersion}
              onChange={(e) => updateField("iosLatestVersion", e.target.value)}
              placeholder="1.0.0"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ios-min">Minimum supported version</Label>
            <Input
              id="ios-min"
              value={form.iosMinSupportedVersion}
              onChange={(e) => updateField("iosMinSupportedVersion", e.target.value)}
              placeholder="1.0.0"
            />
          </div>
        </div>
        <SwitchField
          id="ios-force-update"
          label="Force update"
          description="Require users on older iOS versions to update"
          checked={form.iosForceUpdate}
          onCheckedChange={(checked) => updateField("iosForceUpdate", checked)}
        />
        <div className="space-y-1.5">
          <Label htmlFor="ios-store-url">App Store URL</Label>
          <Input
            id="ios-store-url"
            value={form.iosStoreUrl}
            onChange={(e) => updateField("iosStoreUrl", e.target.value)}
            placeholder="https://apps.apple.com/..."
          />
        </div>
      </SettingsSection>

        <SettingsSection title="Android">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="android-latest">Latest version</Label>
            <Input
              id="android-latest"
              value={form.androidLatestVersion}
              onChange={(e) => updateField("androidLatestVersion", e.target.value)}
              placeholder="1.0.0"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="android-min">Minimum supported version</Label>
            <Input
              id="android-min"
              value={form.androidMinSupportedVersion}
              onChange={(e) => updateField("androidMinSupportedVersion", e.target.value)}
              placeholder="1.0.0"
            />
          </div>
        </div>
        <SwitchField
          id="android-force-update"
          label="Force update"
          description="Require users on older Android versions to update"
          checked={form.androidForceUpdate}
          onCheckedChange={(checked) => updateField("androidForceUpdate", checked)}
        />
        <div className="space-y-1.5">
          <Label htmlFor="android-store-url">Play Store URL</Label>
          <Input
            id="android-store-url"
            value={form.androidStoreUrl}
            onChange={(e) => updateField("androidStoreUrl", e.target.value)}
            placeholder="https://play.google.com/..."
          />
        </div>
      </SettingsSection>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SettingsSection title="Maintenance">
        <SwitchField
          id="maintenance-mode"
          label="Maintenance mode"
          description="Show maintenance screen in the mobile app"
          checked={form.maintenanceMode}
          onCheckedChange={(checked) => updateField("maintenanceMode", checked)}
        />
        <div className="space-y-1.5">
          <Label htmlFor="maintenance-message">Maintenance message</Label>
          <textarea
            id="maintenance-message"
            value={form.maintenanceMessage}
            onChange={(e) => updateField("maintenanceMessage", e.target.value)}
            placeholder="The app is temporarily unavailable..."
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </SettingsSection>

        <SettingsSection title="Support">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="support-phone">Phone number</Label>
            <Input
              id="support-phone"
              value={form.supportPhoneNumber}
              onChange={(e) => updateField("supportPhoneNumber", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-email">Email</Label>
            <Input
              id="support-email"
              type="email"
              value={form.supportEmail}
              onChange={(e) => updateField("supportEmail", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-whatsapp">WhatsApp</Label>
            <Input
              id="support-whatsapp"
              value={form.supportWhatsApp}
              onChange={(e) => updateField("supportWhatsApp", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-hours">Support hours</Label>
            <Input
              id="support-hours"
              value={form.supportHours}
              onChange={(e) => updateField("supportHours", e.target.value)}
              placeholder="Mon–Fri 8am–5pm"
            />
          </div>
        </div>
        </SettingsSection>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SettingsSection title="Links & social">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="privacy-url">Privacy policy URL</Label>
            <Input
              id="privacy-url"
              value={form.privacyPolicyUrl}
              onChange={(e) => updateField("privacyPolicyUrl", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="terms-url">Terms of service URL</Label>
            <Input
              id="terms-url"
              value={form.termsOfServiceUrl}
              onChange={(e) => updateField("termsOfServiceUrl", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="website-url">Website URL</Label>
            <Input
              id="website-url"
              value={form.websiteUrl}
              onChange={(e) => updateField("websiteUrl", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="facebook-url">Facebook URL</Label>
            <Input
              id="facebook-url"
              value={form.facebookUrl}
              onChange={(e) => updateField("facebookUrl", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="instagram-url">Instagram URL</Label>
            <Input
              id="instagram-url"
              value={form.instagramUrl}
              onChange={(e) => updateField("instagramUrl", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="twitter-url">Twitter / X URL</Label>
            <Input
              id="twitter-url"
              value={form.twitterUrl}
              onChange={(e) => updateField("twitterUrl", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="linkedin-url">LinkedIn URL</Label>
            <Input
              id="linkedin-url"
              value={form.linkedInUrl}
              onChange={(e) => updateField("linkedInUrl", e.target.value)}
            />
          </div>
        </div>
        </SettingsSection>

        <SettingsSection title="About">
        <div className="space-y-1.5">
          <Label htmlFor="about-text">About text</Label>
          <textarea
            id="about-text"
            value={form.aboutText}
            onChange={(e) => updateField("aboutText", e.target.value)}
            placeholder="Short description shown in the mobile app"
            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        </SettingsSection>
      </div>

      <div className="flex justify-end pb-4">
        <Button disabled={isSaving} onClick={() => void handleSave()}>
          {isSaving ? "Saving..." : "Save settings"}
        </Button>
      </div>
    </div>
  );
};

export default MobileAppSettingsSection;
